import { Badge, type BadgeVariant } from "@design";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { ContentFilterConfig, RaiPolicyResponse } from "@/api";
import { Text } from "@/components/Text";

const SEVERITY_VARIANT: Record<string, BadgeVariant> = { Low: "success", Medium: "warning", High: "danger" };
const KNOWN_FILTERS = [
  "Hate",
  "Sexual",
  "Violence",
  "Selfharm",
  "Jailbreak",
  "ProtectedMaterialText",
  "ProtectedMaterialCode",
  "Profanity",
] as const;
type KnownFilter = (typeof KNOWN_FILTERS)[number];

function filterKey(name: string): KnownFilter | undefined {
  const compact = name.replace(/\s+/g, "").replace(/^SelfHarm$/, "Selfharm");
  return KNOWN_FILTERS.find((k) => k === compact);
}

function FilterRow({ filter }: Readonly<{ filter: ContentFilterConfig }>) {
  const { t } = useTranslation("adminGovernance");
  const key = filterKey(filter.name);
  let status: { variant: BadgeVariant; label: string };
  if (!filter.enabled) status = { variant: "neutral", label: t("policies.status.disabled") };
  else if (filter.blocking) status = { variant: "danger", label: t("policies.status.blocking") };
  else status = { variant: "info", label: t("policies.status.annotate") };
  return (
    <li className="flex items-center justify-between gap-4 py-3">
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="flex flex-wrap items-center gap-2">
          <Text as="span" variant="body-sm">
            {filter.name}
          </Text>
          <Badge size="compact" variant={status.variant}>
            {status.label}
          </Badge>
        </span>
        {key && (
          <Text as="span" variant="caption" tone="tertiary">
            {t(`filters.${key}`)}
          </Text>
        )}
      </div>
      {filter.severityThreshold ? (
        <Badge variant={SEVERITY_VARIANT[filter.severityThreshold] ?? "neutral"}>{filter.severityThreshold}</Badge>
      ) : (
        <Text as="span" variant="caption" tone="tertiary">
          {t("policies.na")}
        </Text>
      )}
    </li>
  );
}

function FilterGroup({ title, filters }: Readonly<{ title: string; filters: ContentFilterConfig[] }>) {
  if (filters.length === 0) return null;
  return (
    <div className="flex flex-col gap-1">
      <Text variant="eyebrow" tone="tertiary">
        {title}
      </Text>
      <ul className="flex flex-col divide-y divide-border-subtle">
        {filters.map((filter) => (
          <FilterRow key={`${filter.source}-${filter.name}`} filter={filter} />
        ))}
      </ul>
    </div>
  );
}

/** One policy: a summary line that expands into its prompt/completion filters and blocklists. */
export function PolicyItem({ policy }: Readonly<{ policy: RaiPolicyResponse }>) {
  const { t } = useTranslation("adminGovernance");
  const [open, setOpen] = useState(false);
  const filters = policy.contentFilters ?? [];
  const blocklists = policy.customBlocklists ?? [];
  const active = filters.filter((f) => f.enabled).length;
  const blocking = filters.filter((f) => f.enabled && f.blocking).length;
  const Chevron = open ? ChevronDown : ChevronRight;
  return (
    <li className="flex flex-col py-2">
      <button
        type="button"
        aria-expanded={open}
        aria-label={t("policies.showDetails", { name: policy.name })}
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between gap-4 rounded-lg py-2 text-left hover:bg-surface-secondary"
      >
        <span className="flex min-w-0 flex-col gap-0.5">
          <Text as="span" variant="body">
            {policy.name}
          </Text>
          <Text as="span" variant="caption" tone="tertiary">
            {t("policies.summary", { active, blocking, blocklists: blocklists.length })}
          </Text>
        </span>
        <span className="flex shrink-0 items-center gap-2">
          {policy.basePolicyName && (
            <Text as="span" variant="caption" tone="tertiary" className="hidden md:inline">
              {t("policies.base", { name: policy.basePolicyName })}
            </Text>
          )}
          <Chevron size={16} className="text-content-tertiary" aria-hidden />
        </span>
      </button>
      {open && (
        <div className="flex flex-col gap-4 pt-2 pb-2">
          <FilterGroup title={t("policies.prompt")} filters={filters.filter((f) => f.source === "Prompt")} />
          <FilterGroup title={t("policies.completion")} filters={filters.filter((f) => f.source === "Completion")} />
          {blocklists.length > 0 && (
            <div className="flex flex-col gap-1">
              <Text variant="eyebrow" tone="tertiary">
                {t("policies.attachedBlocklists")}
              </Text>
              <ul className="flex flex-col divide-y divide-border-subtle">
                {blocklists.map((bl) => (
                  <li key={bl.blocklistName} className="flex items-center justify-between gap-4 py-2">
                    <Text as="span" variant="body-sm">
                      {bl.blocklistName}
                    </Text>
                    <span className="flex items-center gap-2">
                      <Text as="span" variant="caption" tone="tertiary">
                        {bl.source}
                      </Text>
                      {bl.blocking && (
                        <Badge size="compact" variant="danger">
                          {t("policies.status.blocking")}
                        </Badge>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </li>
  );
}
